package fr.frux.core.designsystem.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing

/**
 * [Destructive] : fond `error`, texte `onError`. [DestructiveText] : sans fond, texte `error`
 * (action de sortie qui perd de la saisie, ex. « Quitter »).
 */
enum class FruxButtonStyle { Filled, Tonal, Outline, Text, Destructive, DestructiveText }

/** Cible tactile minimale (Material 3, WCAG 2.5.8 « Target Size »). */
val FruxMinTouchTarget = 48.dp

/**
 * Bouton-icône aux dimensions accessibles.
 *
 * L'`IconButton` de Material 3 mesure 40 dp depuis la 1.9 : utilisé tel quel, chaque icône
 * d'action de l'app passe sous le seuil des 48 dp — mesuré, pas supposé (cf. les assertions
 * `assertHeightIsAtLeast` des tests d'écran). Passer par ce composant plutôt que par
 * `IconButton` garde la contrainte en un seul endroit.
 */
@Composable
fun FruxIconButton(
    icon: ImageVector,
    contentDescription: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    tint: Color = LocalContentColor.current,
) {
    IconButton(
        onClick = onClick,
        modifier = modifier.size(FruxMinTouchTarget),
        enabled = enabled,
    ) {
        Icon(
            imageVector = icon,
            contentDescription = contentDescription,
            tint = tint,
        )
    }
}

@Composable
fun FruxButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    style: FruxButtonStyle = FruxButtonStyle.Filled,
    enabled: Boolean = true,
    loading: Boolean = false,
    icon: ImageVector? = null,
) {
    val shape = RoundedCornerShape(50)
    val heightModifier = modifier.heightIn(min = 48.dp)
    val isEnabled = enabled && !loading
    val scheme = MaterialTheme.colorScheme

    val content: @Composable () -> Unit = {
        FruxButtonContent(text = text, icon = icon, loading = loading)
    }

    when (style) {
        FruxButtonStyle.Outline -> {
            OutlinedButton(
                onClick = onClick,
                modifier = heightModifier,
                enabled = isEnabled,
                shape = shape,
                colors = ButtonDefaults.outlinedButtonColors(contentColor = scheme.primary),
                border = BorderStroke(width = 1.dp, color = scheme.outlineVariant),
            ) { content() }
        }

        FruxButtonStyle.Text, FruxButtonStyle.DestructiveText -> {
            TextButton(
                onClick = onClick,
                modifier = heightModifier,
                enabled = isEnabled,
                shape = shape,
                colors =
                    ButtonDefaults.textButtonColors(
                        contentColor = if (style == FruxButtonStyle.Text) scheme.primary else scheme.error,
                    ),
            ) { content() }
        }

        FruxButtonStyle.Filled -> {
            FruxFilledButton(onClick, heightModifier, isEnabled, shape, scheme.primary, scheme.onPrimary, content)
        }

        FruxButtonStyle.Tonal -> {
            FruxFilledButton(
                onClick,
                heightModifier,
                isEnabled,
                shape,
                scheme.primaryContainer,
                scheme.onPrimaryContainer,
                content,
            )
        }

        FruxButtonStyle.Destructive -> {
            FruxFilledButton(onClick, heightModifier, isEnabled, shape, scheme.error, scheme.onError, content)
        }
    }
}

@Composable
private fun FruxFilledButton(
    onClick: () -> Unit,
    modifier: Modifier,
    enabled: Boolean,
    shape: Shape,
    container: Color,
    onContainer: Color,
    content: @Composable () -> Unit,
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled,
        shape = shape,
        colors = ButtonDefaults.buttonColors(containerColor = container, contentColor = onContainer),
    ) { content() }
}

@Composable
private fun FruxButtonContent(
    text: String,
    icon: ImageVector?,
    loading: Boolean,
) {
    Row(
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (loading) {
            CircularProgressIndicator(
                modifier = Modifier.size(18.dp),
                color = LocalContentColor.current,
                strokeWidth = 2.dp,
            )
        } else {
            if (icon != null) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp),
                )
                Spacer(Modifier.width(FruxSpacing.sm))
            }
            Text(text = text, style = MaterialTheme.typography.labelLarge)
        }
    }
}
